import { useEffect, useState } from "react";
import { assetUrl } from "../utils/api";

export default function AlbumsPage() {
  const currentUserId = localStorage.getItem("user_id");
  const [photos, setPhotos] = useState([]);
  const [avatar, setAvatar] = useState("");
  const [uploading, setUploading] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [sortMode, setSortMode] = useState(false);
  const [pressTimer, setPressTimer] = useState(null);

  const loadProfile = async () => {
    try {
      const res = await fetch(`/api/profile/${currentUserId}`);
      const data = await res.json();

      if (res.ok) {
        setAvatar(data.avatar || "");
        setPhotos(data.photos || []);
      }
    } catch (err) {
      console.error("加载相册失败:", err);
    }
  };

  useEffect(() => {
    if (currentUserId) {
      loadProfile();
    }
  }, [currentUserId]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (previewIndex === null) return;

      if (e.key === "Escape") {
        setPreviewIndex(null);
      }

      if (e.key === "ArrowLeft") {
        setPreviewIndex((prev) => (prev > 0 ? prev - 1 : prev));
      }

      if (e.key === "ArrowRight") {
        setPreviewIndex((prev) => (prev < photos.length - 1 ? prev + 1 : prev));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewIndex, photos.length]);

  const saveOrder = async (nextPhotos) => {
    try {
      setSavingOrder(true);

      const res = await fetch("/api/photos/reorder", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_user_id: Number(currentUserId),
          photo_ids: nextPhotos.map((p) => p.id),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.detail || "保存排序失败");
        loadProfile();
      }
    } catch (err) {
      console.error("保存排序失败:", err);
      alert("保存排序失败");
      loadProfile();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleUploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const formData = new FormData();
    formData.append("user_id", String(currentUserId));
    files.forEach((file) => {
      formData.append("files", file);
    });

    setUploading(true);

    try {
      const res = await fetch("/api/profile/upload-photos", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.detail || "上传失败");
        return;
      }

      alert("上传成功");
      loadProfile();
    } catch (err) {
      console.error("上传生活照失败:", err);
      alert("上传失败");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeletePhoto = async (photoId) => {
    const ok = window.confirm("确定删除这张照片吗？");
    if (!ok) return;

    try {
      const res = await fetch("/api/photos/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_user_id: Number(currentUserId),
          photo_id: photoId,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.detail || "删除失败");
        return;
      }

      alert("删除成功");
      loadProfile();
      setPreviewIndex(null);
    } catch (err) {
      console.error("删除照片失败:", err);
      alert("删除失败");
    }
  };

  const handleSetAvatar = async (photoId) => {
    try {
      const res = await fetch("/api/photos/set-avatar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_user_id: Number(currentUserId),
          photo_id: photoId,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.detail || "设置头像失败");
        return;
      }

      const fullAvatar = `${data.avatar}`;
      localStorage.setItem("avatar", fullAvatar);
      window.dispatchEvent(new Event("avatarUpdated"));

      alert("头像设置成功");
      loadProfile();
    } catch (err) {
      console.error("设置头像失败:", err);
      alert("设置头像失败");
    }
  };

  const movePhoto = (fromId, toId) => {
    const fromIndex = photos.findIndex((p) => p.id === fromId);
    const toIndex = photos.findIndex((p) => p.id === toId);

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return photos;

    const next = [...photos];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  };

  const handleDragStart = (photoId) => {
    setDraggingId(photoId);
  };

  const handleDragOver = (e, photoId) => {
    e.preventDefault();
    if (dragOverId !== photoId) {
      setDragOverId(photoId);
    }
  };

  const handleDrop = async (targetId) => {
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }

    const nextPhotos = movePhoto(draggingId, targetId);
    setPhotos(nextPhotos);
    setDraggingId(null);
    setDragOverId(null);
    await saveOrder(nextPhotos);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleTouchStart = () => {
    if (!isMobile) return;

    const timer = setTimeout(() => {
      setSortMode(true);
    }, 500);

    setPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;

    const nextPhotos = [...photos];
    [nextPhotos[index - 1], nextPhotos[index]] = [nextPhotos[index], nextPhotos[index - 1]];
    setPhotos(nextPhotos);
    await saveOrder(nextPhotos);
  };

  const handleMoveDown = async (index) => {
    if (index === photos.length - 1) return;

    const nextPhotos = [...photos];
    [nextPhotos[index], nextPhotos[index + 1]] = [nextPhotos[index + 1], nextPhotos[index]];
    setPhotos(nextPhotos);
    await saveOrder(nextPhotos);
  };

  const currentPreviewPhoto =
    previewIndex !== null && photos[previewIndex] ? photos[previewIndex] : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">我的相册</h2>
        <div className="flex gap-2">
          {isMobile && sortMode && (
            <button
              onClick={() => setSortMode(false)}
              className="px-4 py-2 border rounded-lg text-sm"
            >
              完成排序
            </button>
          )}

          <label className="px-4 py-2 bg-pink-500 text-white rounded-lg cursor-pointer text-sm">
            {uploading ? "上传中..." : "上传生活照"}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleUploadPhotos}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-3">当前头像</h3>
        {avatar ? (
          <img
            src={assetUrl(avatar)}
            alt="头像"
            className="w-28 h-28 rounded-full object-cover border"
          />
        ) : (
          <p className="text-gray-400">暂无头像</p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">生活照</h3>
          <span className="text-sm text-gray-400">
            {photos.length} 张{savingOrder ? " · 保存顺序中..." : ""}
          </span>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          {isMobile
            ? "手机端长按照片进入排序模式，再用上移/下移调整顺序；点击图片查看大图"
            : "拖动照片可以调整顺序，点击图片可以查看大图"}
        </p>

        {photos.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-gray-400">
            还没有上传生活照
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((photo, index) => {
              const isDragging = draggingId === photo.id;
              const isDragOver = dragOverId === photo.id && draggingId !== photo.id;

              return (
                <div
                  key={photo.id || index}
                  draggable={!isMobile}
                  onDragStart={() => !isMobile && handleDragStart(photo.id)}
                  onDragOver={(e) => !isMobile && handleDragOver(e, photo.id)}
                  onDrop={() => !isMobile && handleDrop(photo.id)}
                  onDragEnd={() => !isMobile && handleDragEnd()}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchEnd}
                  className={`bg-white border rounded-xl overflow-hidden shadow-sm transition-all ${
                    sortMode ? "ring-2 ring-pink-200" : ""
                  } ${isMobile ? "cursor-default" : "cursor-move"} ${
                    isDragging ? "opacity-40 scale-95 rotate-1" : ""
                  } ${isDragOver ? "ring-2 ring-pink-400 border-pink-400 -translate-y-1" : ""}`}
                >
                  <div
                    className="relative"
                    onClick={() => {
                      if (!sortMode) {
                        setPreviewIndex(index);
                      }
                    }}
                  >
                    <img
                      src={assetUrl(photo.image_url)}
                      alt={`生活照${index + 1}`}
                      className="w-full h-40 object-cover"
                    />
                    {isDragOver && (
                      <div className="absolute inset-0 bg-pink-200/40 flex items-center justify-center text-pink-700 font-semibold">
                        松开后放到这里
                      </div>
                    )}
                  </div>

                  <div className="p-3 space-y-2">
                    <div className="text-xs text-gray-400">第 {index + 1} 张</div>

                    {isMobile && sortMode && (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0 || savingOrder}
                          className="w-full text-sm border rounded-lg py-2 disabled:opacity-40"
                        >
                          上移
                        </button>
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === photos.length - 1 || savingOrder}
                          className="w-full text-sm border rounded-lg py-2 disabled:opacity-40"
                        >
                          下移
                        </button>
                      </div>
                    )}

                    <button
                      onClick={() => handleSetAvatar(photo.id)}
                      className="w-full text-sm text-pink-500 border border-pink-200 rounded-lg py-2 hover:bg-pink-50"
                    >
                      设为头像
                    </button>

                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="w-full text-sm text-red-500 border border-red-200 rounded-lg py-2 hover:bg-red-50"
                    >
                      删除照片
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {currentPreviewPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4"
          onClick={() => setPreviewIndex(null)}
        >
          <div
            className="relative max-w-5xl w-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewIndex(null)}
              className="absolute top-4 right-4 z-10 bg-white/90 text-gray-800 rounded-full w-10 h-10"
            >
              ×
            </button>

            {previewIndex > 0 && (
              <button
                onClick={() => setPreviewIndex((prev) => prev - 1)}
                className="absolute left-4 z-10 bg-white/90 text-gray-800 rounded-full w-10 h-10"
              >
                ‹
              </button>
            )}

            <img
              src={assetUrl(currentPreviewPhoto.image_url)}
              alt="预览大图"
              className="max-h-[80vh] max-w-full rounded-xl shadow-2xl object-contain"
            />

            {previewIndex < photos.length - 1 && (
              <button
                onClick={() => setPreviewIndex((prev) => prev + 1)}
                className="absolute right-4 z-10 bg-white/90 text-gray-800 rounded-full w-10 h-10"
              >
                ›
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
